import React, { useEffect, useState } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	Legend,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
	LineChart,
	Line,
	AreaChart,
	Area,
	LabelList,
} from 'recharts';
import TableLoading from '../TableLoading';
import moment from 'moment';
import {
	formatCamelCaseToText,
	formatCurrency,
	formatCurrencyBare,
	formatCurrencyNew,
	formatHyphenatedText,
	request,
} from '../../services/utilities';
import { Card, Col, Row, Statistic } from 'antd';
import { TimeRangeDropdownAlt3 } from '../TimeRangeDropdownAlt3';
import { set } from 'date-fns';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc0cb', '#faad14'];

export const cardColors = [
	{ bg: 'blue', text: '#fff' },
	{ bg: 'green', text: '#fff' },
	{ bg: 'brown', text: '#fff' },
	{ bg: 'red', text: '#fff' },
];

export const renderPieLabel = ({ name, value, percent }) => {
	const formattedName = String(name).charAt(0).toUpperCase() + String(name).slice(1);
	const formattedValue = formatCurrencyNew(value);
	const formattedPercent = (percent * 100).toFixed(1);
	return `${formattedName}: ${formattedValue} (${formattedPercent}%)`;
};

const mapTimeRangeToApiFilter = range => {
	switch (range) {
		case 'today':
			return 'today';
		case 'pastWeek':
			return 'past-week';
		case 'lastMonth':
			return 'last-month';
		case 'pastQuarter':
			return 'past-quarter';
		case 'pastSixMonths':
			return 'past-six-months';
		case 'pastThreeYears':
			return 'past-three-years';
		case 'past30Days':
			return 'past-month';
		case 'pastYear':
			return 'past-year';
		case 'lastYear':
			return 'last-year';
		case 'allTime':
			return 'all-time';
		default:
			return 'past-month';
	}
};

const mapTimeRangeToApiFilterAlt = range => {
	switch (range) {
		case 'today':
			return 'Today';
		case 'pastWeek':
			return 'Past Week';
		case 'lastMonth':
			return 'Last Month';
		case 'pastQuarter':
			return 'Past Quarter';
		case 'pastSixMonths':
			return 'Past Six Months';
		case 'pastThreeYears':
			return 'Past Three Years';
		case 'past30Days':
			return 'Past 30 Days';
		case 'pastYear':
			return 'Past Year';
		case 'lastYear':
			return 'Last Year';
		case 'allTime':
			return 'All Time';
		default:
			return 'Past 30 Days';
	}
};

const TransactionMetrics = ({ module }) => {
	const [timeRanges, setTimeRanges] = useState({
		staffRanking: 'past30Days',
		appointment: 'past30Days',
		appointmentVsRegistration: 'past30Days',
		appointmentTrends: 'past30Days',
		departmentTrends: 'past30Days',
		admissionRates: 'past30Days',
		appointmentTypes: 'past30Days',
		missedCancelled: 'past30Days',
		patientDemographics: 'past30Days',
		patientAppointmentByAgeDemographics: 'past30Days',
		patientRetention: 'past30Days',
		bedOccupancy: 'past30Days',
		mortalityRate: 'past30Days',
		appointmentStatusMetrics: 'past30Days',
		transactionsMetrics: 'past30Days',
		requestCount: 'past30Days',
	});

	// Individual loading states for each chart
	const [loadingStates, setLoadingStates] = useState({
		staffRanking: false,
		appointment: false,
		appointmentStatusMetrics: false,
		appointmentVsRegistration: false,
		appointmentTrends: false,
		departmentTrends: false,
		admissionRates: false,
		appointmentTypes: false,
		missedCancelled: false,
		patientDemographics: false,
		patientAppointmentByAgeDemographics: false,
		patientRetention: false,
		bedOccupancy: false,
		mortalityRate: false,
		transactionsMetrics: false,
		requestCount: false,
	});

	// Chart data states

	const [appointmentTrends, setAppointmentTrends] = useState([]);
	const [transactionMetrics, setTransactionMetrics] = useState();

	const COLORSe = {
		appointment: '#8884d8',
		registration: '#82ca9d',
	};

	// API fetch functions

	const fetchTransactionsMetrics = async range => {
		const dateFilter = mapTimeRangeToApiFilter(range);
		setLoadingStates(prev => ({ ...prev, transactionsMetrics: true }));
		try {
			const res = await request(`analytics-v2/transaction-service-breakdown?type=${module}&dateFilter=${dateFilter}`, 'GET', true);
			const dataAlt = {
				grandTotal: {
					amount: Math.abs(res?.grandTotal?.amount) || 0,
					amountPaid: Math.abs(res?.grandTotal?.amountPaid) || 0,
				},
				totalsByInsurance: [
					{ name: 'Corporate Amount', value: Math.abs(res?.totalsByInsurance?.Corporate?.amount) || 0 },
					{ name: 'Corporate Amount Resolved', value: Math.abs(res?.totalsByInsurance?.Corporate?.amountPaid) || 0 },
					{ name: 'NHIS Amount ', value: Math.abs(res?.totalsByInsurance?.NHIS?.amount) || 0 },
					{ name: 'NHIS Amount Resolved', value: Math.abs(res?.totalsByInsurance?.NHIS?.amountPaid) || 0 },
					{ name: 'PHIS Amount', value: Math.abs(res.totalsByInsurance?.PHIS?.amount) || 0 },
					{ name: 'PHIS Amount Resolved ', value: Math.abs(res.totalsByInsurance?.PHIS?.amountPaid) || 0 },
					{ name: 'self Pay Amount', value: Math.abs(res?.totalsByInsurance?.['Self Pay']?.amount) || 0 },
					{ name: 'self Pay Amount Resolved', value: Math.abs(res?.totalsByInsurance?.['Self Pay']?.amountPaid) || 0 },
				],

				summary: res?.summary,
			};

			setTransactionMetrics(dataAlt);
		} catch (error) {
			console.error('Failed to fetch age range:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, transactionsMetrics: false }));
		}
	};

	useEffect(() => {
		fetchTransactionsMetrics(timeRanges.transactionsMetrics);
	}, []);

	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));
		switch (chartId) {
			case 'transactionsMetrics':
				fetchTransactionsMetrics(range);
				break;
			default:
				break;
		}
	};

	return (
		<div className="col-lg-12">
			<div className="element-wrapper">
				<h6 className="element-header">Transactions Metrics</h6>

				<div className="d-flex justify-content-end mb-4 w-100">
					<TimeRangeDropdownAlt3 value={timeRanges.transactionsMetrics} onChange={handleTimeRangeChange} chartId="transactionsMetrics" />
				</div>

				<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
					This chart shows transactions metrics over the selected time period.
				</p>

				<div className="card p-4 text-capitalize">
					{loadingStates.transactionsMetrics ? (
						<TableLoading />
					) : transactionMetrics?.summary?.length > 0 ? (
						<div className="row">
							{/* Grand Total Summary */}
							<div className="col-lg-12 col-md-12 mb-4">
								<div className="card p-3 bg-light">
									<h6 className="mb-3">Grand Total Summary</h6>
									<div className="row">
										<div className="col-6">
											<div className="card  p-4">
												<Statistic
													title="Total Amount"
													value={formatCurrencyNew(Math.abs(transactionMetrics?.grandTotal?.amount || 0))}
													valueStyle={{ color: cardColors[0].bg, fontWeight: 'bold' }}
												/>
												<small className="text-muted">
													All services combined &nbsp;
													<strong>{mapTimeRangeToApiFilterAlt(timeRanges?.transactionsMetrics)}</strong>
												</small>
											</div>
										</div>

										<div className="col-6">
											<div className="card  p-4">
												<Statistic
													title="Total Amount Resolved"
													value={formatCurrencyNew(Math.abs(transactionMetrics?.grandTotal?.amountPaid || 0))}
													valueStyle={{ color: cardColors[1].bg, fontWeight: 'bold' }}
												/>
												<small className="text-muted">
													All services combined &nbsp;
													<strong>{mapTimeRangeToApiFilterAlt(timeRanges?.transactionsMetrics)}</strong>
												</small>
											</div>
										</div>
									</div>
								</div>
							</div>

							{/* Insurance Breakdown Chart */}
							<div className="col-lg-12 col-md-12 mb-4">
								<div className="card p-3 h-100">
									<h6 className="mb-3">Total By Insurance</h6>
									<ResponsiveContainer width="100%" height={300}>
										<PieChart>
											<Pie
												data={transactionMetrics?.totalsByInsurance || []}
												cx="50%"
												cy="50%"
												innerRadius={60}
												outerRadius={80}
												paddingAngle={5}
												dataKey="value"
												label={renderPieLabel}
												labelLine
											>
												{transactionMetrics?.totalsByInsurance?.map((entry, index) => (
													<Cell key={`entry-${index}`} fill={COLORS[index % COLORS.length]} />
												))}
											</Pie>
											<Tooltip
												wrapperStyle={{ textTransform: 'capitalize' }}
												formatter={value => Number(value).toLocaleString()}
												nameFormatter={label => label.charAt(0).toUpperCase() + label.slice(1)}
											/>
											<Legend wrapperStyle={{ textTransform: 'capitalize' }} />
										</PieChart>
									</ResponsiveContainer>
								</div>
							</div>

							{/* Summary Metrics - Main Content */}
							<div className="row w-100">
								<h6 className="mb-3 ml-3">Transaction Summary</h6>
								<div className="row">
									{/* Group data by bill source first, then by insurance type */}
									{transactionMetrics?.summary?.map((x, index) => (
										<div className="col-3">
											<div className="card  p-4">
												<Statistic
													title={x?.insuranceType + ' (' + x?.billSource + ')'}
													value={formatCurrencyNew(Math.abs(x?.amount || 0))}
													valueStyle={{ color: COLORS[index % COLORS.length], fontWeight: 'bold' }}
												/>
												<small className="text-muted">
													<strong className=" bolder">{`Resolved Amount: ${formatCurrencyNew(x?.amountPaid) || 0}`}</strong> &nbsp;
													<br />
													{/* All services combined &nbsp;
												<strong style={{textTransform:"lowercase"}}>{mapTimeRangeToApiFilterAlt(timeRanges?.transactionsMetrics)}</strong> */}
												</small>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					) : (
						<div className="w-100 d-flex justify-content-center align-items-center text-muted" style={{ height: '120px' }}>
							<p>No Record for the selected time</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default TransactionMetrics;
